import {
  DocumentDuplicateIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Button, SettingsPanel, Spinner } from "../../../shared";
import { normalizeOperationalTemplate } from "./template.utils";

const EmptyState = ({ title, description }) => (
  <div className="my-10 text-center">
    <DocumentDuplicateIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
    <h3 className="mt-2 font-semibold text-sm">{title}</h3>
    {description && <p className="text-sm text-gray-500">{description}</p>}
  </div>
);

const TemplateListPanel = ({
  title,
  subtitle,
  templates = [],
  isLoading,
  isError,
  addLabel = "Add Template",
  emptyTitle = "No Templates",
  emptyDescription = "Create a template to reuse defaults across jobs and legs.",
  onAdd,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const panelData = templates.map((template) => {
    const normalized = normalizeOperationalTemplate(template);
    const defaultsCount = Object.keys(normalized.defaults || {}).filter(
      (key) => normalized.defaults[key] !== undefined && normalized.defaults[key] !== ""
    ).length;
    const itemsCount = normalized.items?.length || 0;
    const groupCount = normalized.taskTemplateGroups?.length || 0;

    return {
      title: normalized.name,
      subtitle: (
        <div className="space-y-1">
          {normalized.description && <div>{normalized.description}</div>}
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            <span>{defaultsCount} default fields</span>
            {itemsCount > 0 && <span>{itemsCount} service items</span>}
            {groupCount > 0 && <span>{groupCount} checklist groups</span>}
          </div>
        </div>
      ),
      content: (
        <div className="flex items-center justify-end gap-2">
          {onDuplicate && (
            <button
              className="p-1 hover:bg-gray-100 rounded"
              onClick={() => onDuplicate(template)}
              title="Duplicate Template"
            >
              <DocumentDuplicateIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => onEdit(template)}
            title="Edit Template"
          >
            <PencilIcon className="w-4 h-4 text-gray-500" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => onDelete(template)}
            title="Delete Template"
          >
            <TrashIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      ),
    };
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
        <span className="ml-2 text-sm text-gray-500">Loading templates...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading templates</p>
        <p className="text-sm text-gray-500 mt-1">
          Please check your connection and try again.
        </p>
      </div>
    );
  }

  return (
    <SettingsPanel
      title={title}
      subtitle={subtitle}
      data={
        panelData.length
          ? panelData
          : [
              {
                content: (
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                  />
                ),
              },
            ]
      }
      actions={
        <Button size="xs" onClick={onAdd}>
          {addLabel}
        </Button>
      }
    />
  );
};

export default TemplateListPanel;
